import {
  GL_DETAIL_NUMERIC_COLUMNS,
  QBO_GL_DETAIL_TABLE,
  duckDbIdent,
  sqlString,
} from '../quickBooksGlDetailShared'
import {
  buildCfoGlDetailWhereSql,
  type CfoGlDetailQueryState,
} from './cfoGlDetailQuery'

export type CfoGlProfileFieldKind = 'categorical' | 'date' | 'numeric'
export type CfoGlProfileDateGrain = 'day' | 'week' | 'month'
export type CfoGlProfileSortMode = 'count_desc' | 'count_asc' | 'value_asc' | 'value_desc'

export const CFO_GL_PROFILE_NUMERIC_BIN_COUNT = 8
export const CFO_GL_PROFILE_CATEGORICAL_LIMIT = 24
export const CFO_GL_PROFILE_DATE_BIN_LIMIT = 800
export const CFO_GL_PROFILE_BLANK_KEY = ''

export type CfoGlProfileEntry = {
  key: string
  count: number
  amount: number
}

export type CfoGlProfileFieldData = {
  column: string
  kind: CfoGlProfileFieldKind
  grain?: CfoGlProfileDateGrain
  entries: CfoGlProfileEntry[]
  /** Total distinct values in scope (categorical only; entries may be truncated). */
  distinctCount: number
  /** Rows accounted for by truncated tail values (categorical only). */
  moreRows: number
  moreValues: number
  /** Numeric bin geometry (numeric only). */
  binLo: number
  binHi: number
}

export type CfoGlProfileSelection = {
  column: string
  kind: CfoGlProfileFieldKind
  keys: string[]
  grain?: CfoGlProfileDateGrain
  binLo?: number
  binHi?: number
}

/** Active card selections, one per column; values within a card OR together, cards AND together. */
export type CfoGlProfileFilters = Record<string, CfoGlProfileSelection>

export function cfoGlProfileFieldKind(column: string): CfoGlProfileFieldKind {
  if (column === 'txn_date' || column.endsWith('_date')) return 'date'
  if (GL_DETAIL_NUMERIC_COLUMNS.has(column)) return 'numeric'
  return 'categorical'
}

function amountValueExpr(availableColumns: string[]): string {
  return availableColumns.includes('amount')
    ? `COALESCE(TRY_CAST(${duckDbIdent('amount')} AS DOUBLE), 0)`
    : `0`
}

function amountSelectSql(availableColumns: string[]): string {
  return `SUM(${amountValueExpr(availableColumns)})`
}

function andWhere(whereSql: string, predicate: string): string {
  if (!predicate) return whereSql
  return whereSql ? `${whereSql} AND ${predicate}` : `WHERE ${predicate}`
}

const numericValueExpr = (column: string): string => `TRY_CAST(${duckDbIdent(column)} AS DOUBLE)`

function numericBucketExpr(valueExpr: string, lo: string, hi: string): string {
  const bins = CFO_GL_PROFILE_NUMERIC_BIN_COUNT
  return `CAST(COALESCE(LEAST(GREATEST(FLOOR((${valueExpr} - ${lo}) * ${bins}.0 / NULLIF(${hi} - ${lo}, 0)), 0), ${bins - 1}), 0) AS INT)`
}

function dateBinExpr(column: string, grain: CfoGlProfileDateGrain): string {
  // date_trunc yields a TIMESTAMP; cast back to DATE so bin keys are clean
  // 'YYYY-MM-DD' strings usable directly as selection keys and display labels.
  return `CAST(CAST(date_trunc('${grain}', TRY_CAST(${duckDbIdent(column)} AS DATE)) AS DATE) AS VARCHAR)`
}

export function buildCfoGlProfileFieldSql(args: {
  state: CfoGlDetailQueryState
  availableColumns: string[]
  column: string
  grain?: CfoGlProfileDateGrain
  tableName?: string
  selectionPredicate?: string
  /** Fixed bin bounds for numeric highlight queries so buckets match the base pass. */
  binLo?: number
  binHi?: number
}): string {
  const { state, availableColumns, column, tableName = QBO_GL_DETAIL_TABLE } = args
  const kind = cfoGlProfileFieldKind(column)
  const whereSql = andWhere(buildCfoGlDetailWhereSql(state, availableColumns), args.selectionPredicate || '')
  const amountSql = `${amountSelectSql(availableColumns)} AS sum_amount`
  if (kind === 'date') {
    const grain = args.grain || 'week'
    return [
      `SELECT ${dateBinExpr(column, grain)} AS value, COUNT(*) AS row_count, ${amountSql}`,
      `FROM ${duckDbIdent(tableName)}`,
      whereSql,
      `GROUP BY 1`,
      `ORDER BY 1 NULLS FIRST`,
      `LIMIT ${CFO_GL_PROFILE_DATE_BIN_LIMIT}`,
    ].filter(Boolean).join('\n')
  }
  if (kind === 'numeric') {
    const valueExpr = numericValueExpr(column)
    const hasFixedBins = Number.isFinite(args.binLo) && Number.isFinite(args.binHi)
    if (hasFixedBins) {
      const lo = String(args.binLo)
      const hi = String(args.binHi)
      return [
        `SELECT ${numericBucketExpr(valueExpr, lo, hi)} AS value, COUNT(*) AS row_count, ${amountSql}`,
        `FROM ${duckDbIdent(tableName)}`,
        andWhere(whereSql, `${valueExpr} IS NOT NULL`),
        `GROUP BY 1`,
        `ORDER BY 1`,
      ].filter(Boolean).join('\n')
    }
    return [
      `WITH scope AS (`,
      `  SELECT ${valueExpr} AS v, ${amountValueExpr(availableColumns)} AS amt`,
      `  FROM ${duckDbIdent(tableName)}`,
      `  ${whereSql}`,
      `),`,
      `stats AS (SELECT MIN(v) AS lo, MAX(v) AS hi FROM scope WHERE v IS NOT NULL)`,
      `SELECT ${numericBucketExpr('s.v', 'st.lo', 'st.hi')} AS value,`,
      `  COUNT(*) AS row_count, SUM(s.amt) AS sum_amount, MIN(st.lo) AS bin_lo, MAX(st.hi) AS bin_hi`,
      `FROM scope s CROSS JOIN stats st`,
      `WHERE s.v IS NOT NULL`,
      `GROUP BY 1`,
      `ORDER BY 1`,
    ].join('\n')
  }
  const grouped = [
    `SELECT COALESCE(CAST(${duckDbIdent(column)} AS VARCHAR), '') AS value, COUNT(*) AS row_count, ${amountSql}`,
    `FROM ${duckDbIdent(tableName)}`,
    whereSql,
    `GROUP BY 1`,
  ].filter(Boolean).join('\n')
  return [
    `SELECT value, row_count, sum_amount,`,
    `  COUNT(*) OVER () AS distinct_count, SUM(row_count) OVER () AS scope_rows`,
    `FROM (`,
    grouped,
    `) grouped_values`,
    `ORDER BY row_count DESC, value ASC`,
    `LIMIT ${CFO_GL_PROFILE_CATEGORICAL_LIMIT}`,
  ].join('\n')
}

export function parseCfoGlProfileFieldRows(args: {
  column: string
  kind: CfoGlProfileFieldKind
  grain?: CfoGlProfileDateGrain
  rows: Record<string, any>[]
  /** Bin geometry when the query ran with fixed bins (the SQL then omits bin_lo/bin_hi). */
  fixedBinLo?: number
  fixedBinHi?: number
}): CfoGlProfileFieldData {
  const entries: CfoGlProfileEntry[] = []
  let distinctCount = 0
  let scopeRows = 0
  let binLo = Number.isFinite(args.fixedBinLo) ? Number(args.fixedBinLo) : 0
  let binHi = Number.isFinite(args.fixedBinHi) ? Number(args.fixedBinHi) : 0
  for (const row of args.rows) {
    const count = Math.max(0, Math.floor(Number(row.row_count || 0) || 0))
    const amount = Number(row.sum_amount || 0)
    const rawValue = row.value
    const key = rawValue == null ? CFO_GL_PROFILE_BLANK_KEY : String(rawValue)
    if (args.kind === 'numeric') {
      const bucket = Number(key)
      if (!Number.isFinite(bucket)) continue
      const lo = Number(row.bin_lo)
      const hi = Number(row.bin_hi)
      if (Number.isFinite(lo)) binLo = lo
      if (Number.isFinite(hi)) binHi = hi
      entries.push({ key: String(Math.max(0, Math.min(CFO_GL_PROFILE_NUMERIC_BIN_COUNT - 1, Math.floor(bucket)))), count, amount: Number.isFinite(amount) ? amount : 0 })
      continue
    }
    entries.push({ key, count, amount: Number.isFinite(amount) ? amount : 0 })
    const distinct = Number(row.distinct_count)
    const scoped = Number(row.scope_rows)
    if (Number.isFinite(distinct)) distinctCount = Math.max(distinctCount, Math.floor(distinct))
    if (Number.isFinite(scoped)) scopeRows = Math.max(scopeRows, Math.floor(scoped))
  }
  if (args.kind === 'numeric') {
    // Fill empty buckets so the histogram keeps its shape.
    const byKey = new Map(entries.map((entry) => [entry.key, entry]))
    const filled: CfoGlProfileEntry[] = []
    for (let bucket = 0; bucket < CFO_GL_PROFILE_NUMERIC_BIN_COUNT; bucket += 1) {
      filled.push(byKey.get(String(bucket)) || { key: String(bucket), count: 0, amount: 0 })
    }
    return { column: args.column, kind: args.kind, entries: filled, distinctCount: filled.filter((entry) => entry.count > 0).length, moreRows: 0, moreValues: 0, binLo, binHi }
  }
  const shownRows = entries.reduce((total, entry) => total + entry.count, 0)
  const moreValues = args.kind === 'categorical' ? Math.max(0, distinctCount - entries.length) : 0
  const moreRows = args.kind === 'categorical' ? Math.max(0, scopeRows - shownRows) : 0
  return {
    column: args.column,
    kind: args.kind,
    grain: args.grain,
    entries,
    distinctCount: args.kind === 'categorical' ? (distinctCount || entries.length) : entries.length,
    moreRows,
    moreValues,
    binLo: 0,
    binHi: 0,
  }
}

export function buildCfoGlProfileSelectionPredicate(selection: CfoGlProfileSelection): string {
  const column = selection.column
  const expr = duckDbIdent(column)
  if (!selection.keys.length) return ''
  if (selection.kind === 'date') {
    const grain = selection.grain || 'week'
    const blanks = selection.keys.includes(CFO_GL_PROFILE_BLANK_KEY)
    const keys = selection.keys.filter((key) => key !== CFO_GL_PROFILE_BLANK_KEY)
    const parts: string[] = []
    if (keys.length) parts.push(`${dateBinExpr(column, grain)} IN (${keys.map(sqlString).join(', ')})`)
    if (blanks) parts.push(`TRY_CAST(${expr} AS DATE) IS NULL`)
    return parts.length ? `(${parts.join(' OR ')})` : ''
  }
  if (selection.kind === 'numeric') {
    const lo = Number(selection.binLo)
    const hi = Number(selection.binHi)
    const valueExpr = numericValueExpr(column)
    const buckets = selection.keys
      .map((key) => Math.floor(Number(key)))
      .filter((bucket) => Number.isFinite(bucket))
    if (!buckets.length || !Number.isFinite(lo) || !Number.isFinite(hi)) return ''
    return `(${valueExpr} IS NOT NULL AND ${numericBucketExpr(valueExpr, String(lo), String(hi))} IN (${buckets.join(', ')}))`
  }
  const blanks = selection.keys.includes(CFO_GL_PROFILE_BLANK_KEY)
  const values = selection.keys.filter((key) => key !== CFO_GL_PROFILE_BLANK_KEY)
  const parts: string[] = []
  if (values.length) parts.push(`CAST(${expr} AS VARCHAR) IN (${values.map(sqlString).join(', ')})`)
  if (blanks) parts.push(`(${expr} IS NULL OR CAST(${expr} AS VARCHAR) = '')`)
  return parts.length ? `(${parts.join(' OR ')})` : ''
}

/**
 * Compile the card selections into WHERE predicates (AND across cards).
 * Pass `exceptColumn` to facet a card's own distribution: it sees every other
 * card's filter but not its own, so sibling values stay visible and selectable.
 */
export function cfoGlProfileFiltersToPredicates(filters: CfoGlProfileFilters, exceptColumn?: string): string[] {
  return Object.values(filters)
    .filter((selection) => selection.column !== exceptColumn && selection.keys.length)
    .map((selection) => buildCfoGlProfileSelectionPredicate(selection))
    .filter(Boolean)
}

/** Human-readable summary of active card filters for export metadata. */
export function cfoGlProfileFiltersLabel(filters: CfoGlProfileFilters, columnLabel: (column: string) => string): string {
  return Object.values(filters)
    .filter((selection) => selection.keys.length)
    .map((selection) => `${columnLabel(selection.column)}: ${selection.keys.length} value${selection.keys.length === 1 ? '' : 's'}`)
    .join(' | ')
}

const DATE_KEY_RE = /^(\d{4})-(\d{2})-(\d{2})/

function utcDateFromKey(value: string): Date | null {
  const match = DATE_KEY_RE.exec(String(value || '').trim())
  if (!match) return null
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])))
  return Number.isNaN(date.getTime()) ? null : date
}

export function cfoGlProfileNumericBinBounds(data: Pick<CfoGlProfileFieldData, 'binLo' | 'binHi'>, bucket: number): { start: number; end: number } {
  const bins = CFO_GL_PROFILE_NUMERIC_BIN_COUNT
  const span = data.binHi - data.binLo
  if (!Number.isFinite(span) || span <= 0) return { start: data.binLo, end: data.binHi }
  return {
    start: data.binLo + (span * bucket) / bins,
    end: bucket >= bins - 1 ? data.binHi : data.binLo + (span * (bucket + 1)) / bins,
  }
}

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

export function formatCfoGlProfileCompactAmount(value: number): string {
  if (!Number.isFinite(value)) return '0'
  const abs = Math.abs(value)
  const sign = value < 0 ? '-' : ''
  if (abs >= 1_000_000) return `${sign}${(abs / 1_000_000).toFixed(abs >= 10_000_000 ? 0 : 1)}M`
  if (abs >= 1_000) return `${sign}${(abs / 1_000).toFixed(abs >= 10_000 ? 0 : 1)}k`
  if (abs >= 100 || Number.isInteger(value)) return `${sign}${Math.round(abs)}`
  return `${sign}${abs.toFixed(2)}`
}

export function cfoGlProfileEntryLabel(data: CfoGlProfileFieldData, entry: CfoGlProfileEntry, spansYears: boolean): string {
  if (data.kind === 'date') {
    if (entry.key === CFO_GL_PROFILE_BLANK_KEY) return '(no date)'
    const date = utcDateFromKey(entry.key)
    if (!date) return entry.key
    const month = MONTH_NAMES[date.getUTCMonth()]
    if ((data.grain || 'week') === 'month') return `${month} ${date.getUTCFullYear()}`
    const day = `${month} ${date.getUTCDate()}`
    return spansYears ? `${day} ’${String(date.getUTCFullYear()).slice(2)}` : day
  }
  if (data.kind === 'numeric') {
    const bucket = Math.floor(Number(entry.key))
    const bounds = cfoGlProfileNumericBinBounds(data, bucket)
    return `${formatCfoGlProfileCompactAmount(bounds.start)} to ${formatCfoGlProfileCompactAmount(bounds.end)}`
  }
  return entry.key === CFO_GL_PROFILE_BLANK_KEY || entry.key.trim() === '' ? '(blank)' : entry.key
}

export function cfoGlProfileDateSpansYears(data: CfoGlProfileFieldData): boolean {
  if (data.kind !== 'date') return false
  const years = new Set<number>()
  for (const entry of data.entries) {
    const date = utcDateFromKey(entry.key)
    if (date) years.add(date.getUTCFullYear())
  }
  return years.size > 1
}
